import React from 'react';
import { Stack } from '@mui/material';

import { ParallelLine } from '@paragon/contract-library';
import { DistanceField } from '@paragon/material';

import { findAndSetReferenceLine } from '../../../../store/actions/editorActions';
import { startSetReferenceLineGuid } from '../../../../store/actions/insertActions';
import { getAsyncRequestForActiveComponentDesign } from '../../../../store/selectors/getAsyncRequestForActiveComponentDesignSelector';
import { getReferenceLineLongNamesByGuidForActiveComponent } from '../../../../store/selectors/getReferenceLineLongNamesByGuidForActiveComponentSelector';
import { propertiesShouldBeViewOnly } from '../../../../store/selectors/viewOnlyModeSelectors';
import { useAppDispatch, useAppSelector } from '../../../hooks';
import ReferenceLineNameProperty from '../elements/ReferenceLineNameProperty';
import ReferenceLineProperty from '../elements/ReferenceLineProperty';
import { ReferenceLineTypeProperty } from '../elements/ReferenceLineTypeProperty';

interface Props {
  line: ParallelLine;
  afterUpdateLine?: () => void;
}

const ParallelLineProperties = ({ line, afterUpdateLine }: Props) => {
  const dispatch = useAppDispatch();
  const referenceLineLongNamesByGuid = useAppSelector(getReferenceLineLongNamesByGuidForActiveComponent);
  const disabled = useAppSelector((state) => {
    const asyncRequest = getAsyncRequestForActiveComponentDesign(state);
    return propertiesShouldBeViewOnly(state) || (!!asyncRequest && asyncRequest.type !== 'updateFeatures');
  });

  const onApplyOffset = async (newOffset: number) => {
    const newLine = { ...line, offset: newOffset };
    await dispatch(findAndSetReferenceLine(newLine));
    afterUpdateLine && afterUpdateLine();
  };

  if (!referenceLineLongNamesByGuid) {
    throw new Error('Rendering ParallelLineSubPanel but active component reference line names are missing.');
  }

  return (
    <Stack direction="column" spacing={2}>
      <ReferenceLineNameProperty sx={{ marginBottom: '0!important' }} line={line} disabled={disabled} />
      <ReferenceLineTypeProperty line={line} disabled={disabled} />

      <ReferenceLineProperty
        referenceLineLongNamesByGuid={referenceLineLongNamesByGuid}
        line={line}
        disabled={disabled}
        lineKey="lineGuid"
        propertyTitle="Base Line"
        startSetReferenceLineGuid={(referenceLine, key) => {
          dispatch(startSetReferenceLineGuid(referenceLine, key));
        }}
      />

      <DistanceField
        sx={{ m: 1, flex: 1 }}
        fullWidth={false}
        value={line.offset}
        label="Offset"
        onSubmit={onApplyOffset}
        disabled={disabled}
      />
    </Stack>
  );
};

export default ParallelLineProperties;
